import type { UserRole } from '@shared/types/auth';

export const DOCTOR_CONTROL_BASE_PATH = '/admin/doctor-control';

const DOCTOR_PATH_PREFIXES = [
  '/dashboard',
  '/my-day',
  '/patients',
  '/quotes',
  '/calendar',
  '/payments',
  '/settings',
  '/doctor-profile',
  '/organization/manage',
  '/doctor/quick-onboarding',
] as const;

const normalizeBasePath = (value: string): string => value.replace(/\/+$/g, '');

const stripLeadingLocaleSegment = (pathname: string): string => {
  const segments = pathname.split('/').filter(Boolean);
  const [firstSegment, ...restSegments] = segments;

  if (!firstSegment || !/^[a-z]{2}$/i.test(firstSegment)) {
    return pathname || '/';
  }

  return restSegments.length > 0 ? `/${restSegments.join('/')}` : '/';
};

const splitPathAndSuffix = (input: string): { path: string; suffix: string } => {
  const match = input.match(/^([^?#]*)(.*)$/);
  return {
    path: match?.[1] || input,
    suffix: match?.[2] || '',
  };
};

const normalizeDoctorBasePath = (path: string): string => {
  if (path === '/my-day') {
    return '/dashboard';
  }

  return path;
};

const isKnownDoctorPath = (path: string): boolean => {
  const normalizedPath = normalizeDoctorBasePath(
    normalizeBasePath(stripLeadingLocaleSegment(path)) || '/dashboard'
  );

  return DOCTOR_PATH_PREFIXES.some(
    prefix => normalizedPath === prefix || normalizedPath.startsWith(`${prefix}/`)
  );
};

export const isDoctorControlPath = (pathname: string): boolean => {
  const normalized = normalizeBasePath(stripLeadingLocaleSegment(pathname));
  return (
    normalized === DOCTOR_CONTROL_BASE_PATH || normalized.startsWith(`${DOCTOR_CONTROL_BASE_PATH}/`)
  );
};

export const stripDoctorControlPath = (pathname: string): string | null => {
  const delocalizedPathname = stripLeadingLocaleSegment(pathname);
  if (!isDoctorControlPath(delocalizedPathname)) {
    return null;
  }

  const relative = delocalizedPathname.slice(DOCTOR_CONTROL_BASE_PATH.length) || '/dashboard';
  const normalized = relative.startsWith('/') ? relative : `/${relative}`;
  return normalizeDoctorBasePath(normalized);
};

export const isDoctorWorkspacePath = (input: string): boolean => {
  const { path } = splitPathAndSuffix(input);
  return isKnownDoctorPath(path || '/dashboard');
};

export const toDoctorWorkspacePath = (input: string): string => {
  const { path, suffix } = splitPathAndSuffix(input);
  const normalizedPath = normalizeDoctorBasePath(path || '/dashboard');

  if (isDoctorControlPath(normalizedPath)) {
    return `${stripDoctorControlPath(normalizedPath) ?? '/dashboard'}${suffix}`;
  }

  return `${normalizedPath}${suffix}`;
};

export const resolveDoctorWorkspacePath = (input: string, delegated: boolean): string =>
  delegated ? toDoctorWorkspacePath(input) : input;

export const getDoctorWorkspaceReturnPath = (role?: UserRole): string => {
  if (role === 'super_admin') {
    return '/super-admin/users';
  }

  return '/admin/users';
};

export const getDoctorWorkspaceHomePath = (delegated: boolean): string =>
  delegated ? '/dashboard' : '/dashboard';
